/**
 * Presets — design doc §12.4.
 */
import { sortRoles, type RoleId } from "./roles.ts";
import type { PresetId } from "./schema.ts";

export interface Preset {
  readonly id: PresetId;
  readonly name: string;
  readonly description: string;
  readonly playerCount: number;
  /** Delegate roles, in catalogue order. */
  readonly delegates: readonly RoleId[];
  /** Syndicate roles, in catalogue order. */
  readonly syndicate: readonly RoleId[];
}

function times(role: RoleId, n: number): RoleId[] {
  return Array.from({ length: n }, () => role);
}

function preset(p: Preset): Preset {
  return { ...p, delegates: sortRoles(p.delegates), syndicate: sortRoles(p.syndicate) };
}

export const PRESETS: Readonly<Record<PresetId, Preset>> = {
  "first-gala": preset({
    id: "first-gala",
    name: "First Gala",
    description: "Teaching game. No Forger, so every report is true.",
    playerCount: 8,
    delegates: ["analyst", "medic", "sentinel", "tracker", "attache", "attache"],
    syndicate: ["operative", "chameleon"],
  }),
  "intimate-dinner": preset({
    id: "intimate-dinner",
    name: "Intimate Dinner",
    description: "A Short Gala for six.",
    playerCount: 6,
    delegates: ["analyst", "medic", "sentinel", "attache"],
    syndicate: ["operative", "chameleon"],
  }),
  "standard-gala": preset({
    id: "standard-gala",
    name: "Standard Gala",
    description: "The Forger arrives: any report might be false.",
    playerCount: 10,
    delegates: ["analyst", "auditor", "medic", "tracker", "sentinel", "attache", "attache"],
    syndicate: ["operative", "framer", "forger"],
  }),
  "grand-ball": preset({
    id: "grand-ball",
    name: "Grand Ball",
    description: "A public Magistrate and a crowded room.",
    playerCount: 12,
    delegates: [
      "analyst",
      "auditor",
      "medic",
      "tracker",
      "sentinel",
      "magistrate",
      "attache",
      "attache",
    ],
    syndicate: ["operative", "chameleon", "framer", "forger"],
  }),
  masquerade: preset({
    id: "masquerade",
    name: "Masquerade",
    description: "For experienced groups: the Rogue and the Confessor.",
    playerCount: 10,
    delegates: ["analyst", "auditor", "medic", "tracker", "confessor", "rogue", "attache"],
    syndicate: ["operative", "chameleon", "forger"],
  }),
  "embassy-ball": preset({
    id: "embassy-ball",
    name: "Embassy Ball",
    description: "A large game with a Grand Council.",
    playerCount: 16,
    delegates: [
      ...times("analyst", 2),
      "auditor",
      "medic",
      "tracker",
      "sentinel",
      "archivist",
      "magistrate",
      ...times("attache", 3),
    ],
    syndicate: [...times("operative", 2), "chameleon", "framer", "forger"],
  }),
  "state-banquet": preset({
    id: "state-banquet",
    name: "State Banquet",
    description: "The full twenty-guest gala with a Grand Council.",
    playerCount: 20,
    delegates: [
      ...times("analyst", 2),
      "auditor",
      ...times("medic", 2),
      "tracker",
      "sentinel",
      "archivist",
      "magistrate",
      ...times("attache", 5),
    ],
    syndicate: [...times("operative", 3), "chameleon", "framer", "forger"],
  }),
};

export function presetRoles(preset: Preset): RoleId[] {
  return sortRoles([...preset.delegates, ...preset.syndicate]);
}
